import { useContext } from 'react';

import { formatMoney } from '@betterment/core';
import { useMatchMedia } from '@betterment/js-runtime';
import { Box, Card, Heading, Separator, Spinner, Text } from '@betterment/samba-react';
import { CardLoader, ErrorBoundary } from '@betterment/shared-components';

import { SpendingPowerChart } from './SpendingPowerChart';
import { SpendingPowerHighlight } from './SpendingPowerHighlight';

import { CompletionConfidence } from '../../../shared/components/CompletionConfidence';
import { ForecasterPurposeInputsContext } from '../../context/ForecasterPurposeInputsContext';
import { useForecasterPurposeProjection } from '../../context/ForecasterPurposeProjectionContext';

const RetirementSpendingPowerCardView = () => {
  const isLargeScreen = useMatchMedia('atLeastMedium');

  const data = useForecasterPurposeProjection();
  const inputsState = useContext(ForecasterPurposeInputsContext);

  if (!data || !data.purpose) return <CardLoader />;

  const { isLoading, purpose } = data;

  if (purpose.__typename !== 'PurposeRetirementGrowth' || !purpose.retirementGrowthProjection) {
    return null;
  }

  const targetMonthlySpendingAmount =
    inputsState?.projectionQueryInputs?.retirementGrowth?.targetMonthlySpendingAmount ??
    purpose.targetMonthlySpendingAmount;

  if (!targetMonthlySpendingAmount) return null;

  const { retirementGrowthProjection: projection } = purpose;
  const { completion, monthlySpendingAmount: projectedMonthlySpendingAmount } = projection;

  return (
    <Card css={{ position: 'relative' }}>
      {isLoading && (
        <Box
          css={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1,
            backgroundColor: 'primitives.white.100alpha30',
          }}
        >
          <Spinner />
        </Box>
      )}
      <Card.Header css={{ padding: 'space.300', display: 'flex', flexDirection: 'column', gap: 'space.100' }}>
        <Heading variant="title100">Retirement spending power</Heading>
        {completion && isLargeScreen && <CompletionConfidence {...completion} />}
      </Card.Header>
      <Separator css={{ fullWidthWithInset: 'space.300' }} />
      <Card.Content css={{ padding: 'space.300' }}>
        <Box css={{ display: 'flex', flexDirection: isLargeScreen ? 'row' : 'column', gap: 'space.300' }}>
          <SpendingPowerChart
            projectedMonthlySpendingAmountCents={projectedMonthlySpendingAmount.cents}
            targetMonthlySpendingAmountCents={targetMonthlySpendingAmount.cents}
          />
          <Box css={{ display: 'grid', gap: isLargeScreen ? 'space.100' : 'space.300' }}>
            <Box
              css={{
                display: 'flex',
                flexDirection: isLargeScreen ? 'row' : 'column',
                gap: isLargeScreen ? 'space.400' : 'space.200',
              }}
            >
              <SpendingPowerHighlight
                title="Projected spending power"
                value={formatMoney(projectedMonthlySpendingAmount, 'defaultDollar')}
                subtitle="In today's dollars"
                legendColor="dataviz.blue.2"
              />
              <SpendingPowerHighlight
                title="Desired spending power"
                value={formatMoney(targetMonthlySpendingAmount, 'defaultDollar')}
                subtitle="In today's dollars"
                legendColor="dataviz.navy.1"
              />
            </Box>
            <Text variant="text50" color="text.weak" css={{ paddingLeft: 'space.200' }}>
              Prices rise over time (inflation), and taxes will reduce what you can spend. This estimate shows how much
              your retirement savings would feel like today.
            </Text>
          </Box>
        </Box>
      </Card.Content>
    </Card>
  );
};

export const RetirementSpendingPowerCard = () => {
  return (
    <ErrorBoundary fallbackType="none">
      <RetirementSpendingPowerCardView />
    </ErrorBoundary>
  );
};
